import { defineComponent as _defineComponent } from 'vue'
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'FlatRent',
  props: {
    title: null,
    offers: null,
    seasonNumber: null
  },
  setup(__props: any) {

const props = __props as {
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
};

const { language } = storeToRefs(useLanguageStore());



const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Tällä hetkellä voit katsoa elokuvan',
					'suoratoistona palvelussa'
				),
				getOfferTextBlock(MonetizationType.Rent, ' tai vuokrata sen netissä palvelussa'),
				'. ',
			];
		case 'ru':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В настоящее время Вы можете посмотреть', 'на'),
				getOfferTextBlock(MonetizationType.Rent, ' или взять напрокат на', 'онлайн'),
				'. ',
			];
		case 'it':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Adesso puoi guardare', 'in streaming su '),
				getOfferTextBlock(MonetizationType.Rent, ' oppure noleggiarlo online su'),
				'. ',
			];
		case 'de':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Du kannst', 'bei', 'legal im Stream anschauen'),
				getOfferTextBlock(MonetizationType.Rent, ' oder bei', 'online leihen'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Você pode assistir', 'com assinatura no'),
				getOfferTextBlock(MonetizationType.Rent, ' ou no', 'alugando online'),
				'. ',
			];
		case 'fr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'En ce moment, vous pouvez regarder', 'en streaming su'),
				getOfferTextBlock(MonetizationType.Rent, ' ou de louer sur', 'en ligne'),
				'. ',
			];
		case 'es':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Actualmente, usted es capaz de ver', 'streaming en'),
				getOfferTextBlock(MonetizationType.Rent, ' o para alquilar en', 'online'),
				'. ',
			];
		case 'ja':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'は', 'で配信中です。'),
				getOfferTextBlock(MonetizationType.Rent, '', 'で配信版のレンタルができます'),
				'。',
			];
		case 'ko':
			return [
				getOfferTextBlock(MonetizationType.Flatrate, '현재 ', `에서 "${props.title}" 스트리밍 서비스 `),
				getOfferTextBlock(MonetizationType.Rent, '중이며 ', '에서 대여가 가능합니다.'),
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'În prezent, puteți viziona', 'streaming pe'),
				getOfferTextBlock(MonetizationType.Rent, ' sau să-l închiriați pe', 'online'),
				'. ',
			];
		case 'hu':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Jelenleg a(z)', 'adatfolyamként elérhető a(z)'),
				getOfferTextBlock(MonetizationType.Rent, ' szolgáltatónál, vagy online kibérelhető itt:'),
				'. ',
			];
		case 'cs':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktuálně máte možnost sledovat', 'streaming u'),
				getOfferTextBlock(MonetizationType.Rent, ' nebo si jej půjčit u', 'online'),
				'. ',
			];
		case 'bg':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'В момента можете да гледате стрийминг на', 'в', 'онлайн'),
				getOfferTextBlock(MonetizationType.Rent, ' или да го наемете', 'онлайн'),
				'. ',
			];
		case 'ar':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'يمكنكم حالياً مشاهدة', 'على'),
				getOfferTextBlock(MonetizationType.Rent, ' أو تأجيره على', 'أونلاين'),
				'. ',
			];
		case 'pt':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Atualmente você pode assistir', 'em streaming via'),
				getOfferTextBlock(MonetizationType.Rent, ' ou alugar online via'),
				'. ',
			];
		case 'tr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Şu an', 'adlı yapımı'),
				getOfferTextBlock(
					MonetizationType.Rent,
					' üzerinden izleyebilir veya',
					'üzerinden kiralayıp çevrimiçi olarak seyrdebilirsiniz'
				),
				'. ',
			];
		case 'sq':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Aktualisht, ju mund ta shikoni', 'në'),
				getOfferTextBlock(MonetizationType.Rent, ' ose ta merrni me qira në', 'online'),
				'. ',
			];
		case 'pl':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Obecnie możesz oglądać', 'w streamingu w serwisie'),
				getOfferTextBlock(MonetizationType.Rent, ' lub wypożyczyć go online w serwisie'),
				'. ',
			];
		case 'ro':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Acum poți să vezi', 'pe'),
				getOfferTextBlock(MonetizationType.Rent, ' sau să-l închiriezi online pe'),
				'. ',
			];
		case 'az':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Hazırda siz', 'üzərindən'),
				getOfferTextBlock(
					MonetizationType.Rent,
					' yayımını izləyə və ya',
					'saytında onlayn icarəyə götürə bilərsiniz'
				),
				'. ',
			];
		case 'el':
			return [
				getInfoTextBlock(
					MonetizationType.Flatrate,
					'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
					'με streaming στο'
				),
				getOfferTextBlock(MonetizationType.Rent, ' ή να τον ενοικιάσετε', 'online'),
				'. ',
			];
		case 'sr':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Trenutno možete da gledate', 'strim na'),
				getOfferTextBlock(MonetizationType.Rent, ' ili da ga iznajmite na', 'onlajn'),
				'. ',
			];
		case 'sv':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'För närvarande kan du se', 'streama på'),
				getOfferTextBlock(MonetizationType.Rent, ' eller hyra den på', 'online'),
				'. ',
			];
		case 'uk':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Наразі ви можете дивитися трансляцію', 'на'),
				getOfferTextBlock(MonetizationType.Rent, ' або взяти її напрокат онлайн на'),
				'. ',
			];
		case 'ur':
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'فی الحال آپ', 'کو'),
				getOfferTextBlock(
					MonetizationType.Rent,
					' پر دیکھنے کے قابل  ہیں یا اسے ',
					'پر آن لائن کرائے پر لے سکتے ہیں۔'
				),
			];
		case 'zh':
			return [
				getRTLInfoTextBlock(MonetizationType.Flatrate, '目前你可以在', '观看', ','),
				getOfferTextBlock(MonetizationType.Rent, ' 或在', '在线租赁'),
			];
		case 'en':
		default:
			return [
				getInfoTextBlock(MonetizationType.Flatrate, 'Currently you are able to watch', 'streaming on'),
				getOfferTextBlock(MonetizationType.Rent, ' or rent it on', 'online'),
				'. ',
			];
	}
});

return { __sfc: true,language, props, getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock, text }
}

})